import React, { useEffect, useState } from "react";
import { Phone, DeviceMobile, EnvelopeSimple, User } from "@phosphor-icons/react";
import axios from "../../utils/axiosInstance";

const API = `${window.location.origin}/api`;

function PhoneLink({ number, icon }) {
  if (!number) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <a
      href={`tel:${number.replace(/\s+/g, "")}`}
      className="inline-flex items-center gap-1.5 text-foreground hover:text-blue-800 hover:underline text-sm font-medium"
    >
      {icon}
      {number}
    </a>
  );
}

export default function KontaktyTab() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/kontakty`);
        if (!cancelled) setContacts(data || []);
      } catch (e) {
        console.error("Failed to load kontakty", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const s = search.trim().toLowerCase();
  const filtered = !s
    ? contacts
    : contacts.filter((c) => {
      const hay = [
        c.name,
        c.imie_nazwisko,
        c.numer_telefonu,
        c.numer_telefonu_zebra,
        c.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(s);
    });

  if (loading) {
    return <div className="text-center py-12 text-slate-500">Ładowanie kontaktów...</div>;
  }

  return (
    <div className="max-w-[1200px] mx-auto px-3 py-4 sm:px-6 sm:py-6 space-y-4 sm:space-y-6" data-testid="kontakty-container">
      <div>
        <h2 className="text-xl sm:text-3xl font-bold text-slate-800">Kontakty</h2>
        <p className="text-xs sm:text-base text-slate-600 mt-1">
          Lista kontaktów wszystkich pracowników. Kliknij numer aby zadzwonić.
        </p>
      </div>

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Szukaj po nazwie, telefonie lub emailu..."
        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
        data-testid="kontakty-search"
      />

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center text-slate-500 shadow-sm border border-slate-200">
          {s ? "Brak wyników dla tego zapytania." : "Brak kontaktów."}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold text-slate-600 uppercase">
                  <th className="px-4 py-3">Skrót</th>
                  <th className="px-4 py-3">Imię i nazwisko</th>
                  <th className="px-4 py-3">Komórka</th>
                  <th className="px-4 py-3">Zebra</th>
                  <th className="px-4 py-3">Email</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50" data-testid={`kontakt-row-${c.id}`}>
                    <td className="px-4 py-3 font-mono font-bold text-slate-700">{c.name}</td>
                    <td className="px-4 py-3 text-slate-800">
                      {c.imie_nazwisko || <span className="text-slate-400 italic text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <PhoneLink number={c.numer_telefonu} icon={<Phone className="w-3.5 h-3.5" weight="bold" />} />
                    </td>
                    <td className="px-4 py-3">
                      <PhoneLink number={c.numer_telefonu_zebra} icon={<DeviceMobile className="w-3.5 h-3.5" weight="bold" />} />
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">
                      {c.email ? (
                        <a href={`mailto:${c.email}`} className="hover:underline">{c.email}</a>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filtered.map((c) => (
              <div key={c.id} className="bg-white rounded-xl p-4 shadow-sm border border-slate-200" data-testid={`kontakt-card-${c.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded text-xs">{c.name}</span>
                    </div>
                    {c.imie_nazwisko && (
                      <div className="text-sm font-semibold text-slate-800 mt-1 flex items-center gap-1.5">
                        <User className="w-4 h-4 text-slate-500" />
                        {c.imie_nazwisko}
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5 mt-2">
                  {c.numer_telefonu && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-16 shrink-0">Komórka:</span>
                      <PhoneLink number={c.numer_telefonu} icon={<Phone className="w-3.5 h-3.5" weight="bold" />} />
                    </div>
                  )}
                  {c.numer_telefonu_zebra && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-16 shrink-0">Zebra:</span>
                      <PhoneLink number={c.numer_telefonu_zebra} icon={<DeviceMobile className="w-3.5 h-3.5" weight="bold" />} />
                    </div>
                  )}
                  {c.email && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-16 shrink-0">Email:</span>
                      <a href={`mailto:${c.email}`} className="text-foreground hover:underline text-xs inline-flex items-center gap-1">
                        <EnvelopeSimple className="w-3.5 h-3.5" />
                        {c.email}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
